import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmail, resetPassword, checkUserRole, signOut } from '../services/firebase';
import { theme } from '../styles/theme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmail(email, password);
      const uid = userCredential.user.uid;
      
      // Verificar se o usuário tem permissão de admin
      const role = await checkUserRole(uid);
      
      if (role !== 'admin') {
        // Se não for admin, fazer logout e mostrar erro
        await signOut();
        setError('Acesso negado. Apenas administradores podem fazer login.');
        setLoading(false);
        return;
      }
      
      // Se for admin, permitir acesso
      setTimeout(() => {
        navigate('/home');
      }, 100);
    } catch (err: any) {
      
      let errorMessage = 'Erro ao fazer login. Tente novamente.';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'Usuário não encontrado.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Senha incorreta.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido.';
      } else if (err.code === 'auth/invalid-credential') {
        errorMessage = 'Credenciais inválidas. Verifique email e senha.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Por favor, insira seu email primeiro.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await resetPassword(email);
      setResetSent(true);
      alert('Email de redefinição de senha enviado! Verifique sua caixa de entrada.');
    } catch (err: any) {
      setError('Erro ao enviar email de redefinição. Verifique o email digitado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <h1 style={styles.title}>Meu Negócio</h1>
        
        {resetSent && (
          <div style={styles.successMessage}>
            <CheckCircleIcon style={{ marginRight: 8, fontSize: 18 }} /> Email enviado! Verifique sua caixa de entrada.
          </div>
        )}
        
        <form onSubmit={handleLogin} style={styles.form}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeButton}
              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
          <button 
            type="button" 
            onClick={handleForgotPassword} 
            style={styles.forgotButton}
            disabled={loading}
          >
            Esqueci minha senha
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: theme.gradient,
  },
  loginBox: {
    background: theme.colors.white,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  title: {
    textAlign: 'center',
    color: theme.colors.primary,
    marginBottom: '30px',
    fontSize: '32px',
    fontWeight: 'bold',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s',
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    padding: '12px',
    paddingRight: '50px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s',
    width: '100%',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    background: theme.gradient,
    color: theme.colors.white,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  forgotButton: {
    padding: '10px',
    background: 'transparent',
    border: 'none',
    color: theme.colors.primary,
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  successMessage: {
    background: '#d4edda',
    color: '#155724',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '15px',
    textAlign: 'center',
    fontSize: '14px',
  },
  error: {
    color: theme.colors.error,
    textAlign: 'center',
    fontSize: '14px',
  },
};

export default Login;